import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { Mic, Play, Clock, FileText, Users, ArrowRight, Zap } from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_SESSIONS = [
  { id: 'AMB-001', patient: 'John Smith', patientId: 'P001', date: '2026-02-03', duration: '12:34', status: 'completed', noteGenerated: true, credits: 13 },
  { id: 'AMB-002', patient: 'Maria Garcia', patientId: 'P002', date: '2026-02-03', duration: '8:22', status: 'completed', noteGenerated: true, credits: 9 },
  { id: 'AMB-003', patient: 'Robert Johnson', patientId: 'P003', date: '2026-02-02', duration: '15:07', status: 'completed', noteGenerated: true, credits: 16 },
  { id: 'AMB-004', patient: 'Emily Davis', patientId: 'P004', date: '2026-02-02', duration: '6:45', status: 'completed', noteGenerated: false, credits: 7 },
  { id: 'AMB-005', patient: 'Sarah Williams', patientId: 'P005', date: '2026-02-01', duration: '20:11', status: 'completed', noteGenerated: true, credits: 21 },
];

const VOICE_COMMANDS = [
  { command: '"Lumina, mark that as assessment"', desc: 'Moves dictation to Assessment section' },
  { command: '"Lumina, add medication change"', desc: 'Flags a medication update in the note' },
  { command: '"Lumina, start physical exam"', desc: 'Switches to Objective section' },
  { command: '"Lumina, note allergy"', desc: 'Records an allergy mention' },
  { command: '"Lumina, pause recording"', desc: 'Pauses the ambient session' },
  { command: '"Lumina, generate note"', desc: 'Finalizes and generates SOAP note' },
];

const LuminaAmbient = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();

  return (
    <div className="space-y-6" data-testid="ambient-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Ambient Listening</h1>
          <p className="text-slate-500 mt-1">AI captures conversations and generates SOAP notes &middot; 1 credit/min</p>
        </div>
        <Button onClick={() => navigate('/lumina/patients')} className="bg-black hover:bg-gray-800 gap-2" data-testid="new-ambient-session-btn">
          <Mic className="h-4 w-4" /> New Session
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-violet-200 bg-violet-50/40">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
            <Mic className="h-5 w-5 text-violet-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-800">Start ambient sessions from any patient chart</p>
            <p className="text-xs text-muted-foreground">Open a patient's chart and click "Start Ambient Visit" to begin recording. The session stays within the chart for seamless documentation.</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Session History */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Session History</CardTitle>
              <CardDescription>{DEMO_SESSIONS.length} recordings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {DEMO_SESSIONS.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 rounded-xl border hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/lumina/patients/${session.patientId}`)}
                  data-testid={`session-${session.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-violet-50 flex items-center justify-center">
                      <Mic className="h-5 w-5 text-violet-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{session.patient}</p>
                      <p className="text-xs text-muted-foreground">{session.date} &middot; {session.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{session.credits} credits</span>
                    {session.noteGenerated ? (
                      <Badge className="bg-violet-50 text-violet-700 text-xs gap-1"><FileText className="h-3 w-3" /> Note Generated</Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); navigate('/lumina/soap-notes'); }}>
                        <FileText className="h-3 w-3" /> Generate Note
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel */}
        <div className="space-y-4">
          {/* Voice Commands */}
          <Card className="border-violet-200 bg-violet-50/30" data-testid="voice-commands-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Zap className="h-4 w-4 text-violet-600" /> Voice Commands</CardTitle>
              <CardDescription className="text-xs">Say these during recording for hands-free control</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {VOICE_COMMANDS.map((vc, i) => (
                <div key={i} className="p-2 rounded-lg hover:bg-violet-100/50 transition-colors">
                  <p className="text-sm font-medium font-mono text-violet-800">{vc.command}</p>
                  <p className="text-xs text-muted-foreground">{vc.desc}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Session Stats */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">This Week</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Sessions</span><span className="font-medium">{DEMO_SESSIONS.length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Time</span><span className="font-medium">62 min</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Credits Used</span><span className="font-medium">66</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Notes Generated</span><span className="font-medium">{DEMO_SESSIONS.filter(s => s.noteGenerated).length}</span></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LuminaAmbient;
